import type { ReactNode } from 'react';
import { CornerGuitar } from '../CornerGuitar.tsx';
import { themeOf } from './cmsTheme.ts';
import type { PageSectionTheme, GuitarCorner } from '../../lib/types.ts';

/** Inner-container max-width options used by the CMS sections. */
const WIDTH_CLASS = {
  /** The v2 content measure. Everything uses this unless it has a reason not to. */
  full: 'max-w-[1200px]',
  /** Narrower measures for sections that read as a single column (price lists, a map). */
  '5xl': 'max-w-5xl',
  '3xl': 'max-w-3xl',
} as const;

export interface CmsSectionShellProps {
  /** Section theme — drives the background color and the guitar watermark fill. */
  readonly theme: PageSectionTheme;
  /** When set, renders a decorative CornerGuitar pinned to this corner. */
  readonly guitar?: GuitarCorner | null;
  /** Inner-container max width. Defaults to 'full'. */
  readonly width?: keyof typeof WIDTH_CLASS;
  /** Guitar watermark opacity utility. Defaults to 'opacity-10'. */
  readonly guitarOpacityClass?: string;
  /** Extra classes appended to the inner container (e.g. 'text-center …'). */
  readonly containerClassName?: string;
  /** Anchor id on the outer section element, for hardcoded nav anchors (e.g. #tidsplan). */
  readonly id?: string;
  readonly children: ReactNode;
}

/**
 * The shared skeleton for CMS page sections: a full-bleed themed background with the v2
 * vertical rhythm, an optional CornerGuitar watermark, and a centered, horizontally-padded
 * inner container.
 *
 * The rhythm is dense — 40px on phones, 64px from md up, against v1's flat 80px — because a
 * page is now read as a stack of blocks rather than a series of full-height statements. The
 * guitar watermark sits behind that, in the combination's own `guitar` fill: yellow on the dark
 * bases, green on the light ones, so it reads as texture rather than as a shape.
 *
 * Section content (headings, grids, rich text, loading/error state) is passed as children.
 * CmsHero deliberately does NOT use this — it has a bespoke, fixed-palette layout with a
 * full-bleed background image.
 */
export function CmsSectionShell({
  theme,
  guitar,
  width = 'full',
  guitarOpacityClass = 'opacity-10',
  containerClassName,
  id,
  children,
}: CmsSectionShellProps) {
  const t = themeOf(theme);
  const container =
    `relative ${WIDTH_CLASS[width]} mx-auto px-5 md:px-8` +
    (containerClassName ? ` ${containerClassName}` : '');

  return (
    <section id={id} className={`relative ${t.bg} py-10 md:py-16 overflow-hidden`}>
      {guitar && <CornerGuitar corner={guitar} colorClass={t.guitar} opacityClass={guitarOpacityClass} />}
      <div className={container}>{children}</div>
    </section>
  );
}
